import { Stack, useTheme } from "@mui/material";
import { useAtomValue } from "jotai";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { BalanceBreakdownRow } from "web_myplann/Finances/Components/BalanceBreakdownRow";
import { BalanceHero } from "web_myplann/Finances/Components/BalanceHero";
import { BalanceRecommendation } from "web_myplann/Finances/Components/BalanceRecommendation";
import { FinancesWizardLayout } from "web_myplann/Finances/Components/FinancesWizardLayout";
import { LeaveFinancesDrawer } from "web_myplann/Finances/Components/LeaveFinancesDrawer";
import { isPositiveFinancialBalance } from "web_myplann/Finances/Helpers/isPositiveFinancialBalance";
import { sumIncomeAmounts } from "web_myplann/Finances/Helpers/sumIncomeAmounts";
import { sumMonthlyPayments } from "web_myplann/Finances/Helpers/sumMonthlyPayments";
import { useFinancesWizardNavigation } from "web_myplann/Finances/Hooks/useFinancesWizardNavigation";
import { useFinishFinances } from "web_myplann/Finances/Hooks/useFinishFinances";
import { useLeaveFinancesGuard } from "web_myplann/Finances/Hooks/useLeaveFinancesGuard";
import { financeState } from "web_myplann/Finances/State/FinanceAtom";
import { IncomeBackTo } from "web_myplann/Finances/Types/FinancesNavigationState";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { FooterActions } from "web_myplann/Shared/Components/FooterActions";
import { SectionHeader } from "web_myplann/Shared/Components/SectionHeader";

const R = Resources.Finances;

export const FinancesOverviewV2 = () => {
	const { t } = useResource();
	const theme = useTheme();
	const { navigationState, goToStep } = useFinancesWizardNavigation();
	const { isLeaving, stay, leave, runUnguarded } = useLeaveFinancesGuard();
	const { finishFinances, isPending } = useFinishFinances({ runUnguarded });
	const finance = useAtomValue(financeState);

	const totalIncome = sumIncomeAmounts(finance.income);
	const totalExpenses = sumMonthlyPayments(finance.expenses);
	const balance = totalIncome - totalExpenses;
	const positive = isPositiveFinancialBalance(balance);

	const goToIncome = () =>
		goToStep(Routing.Finances.Income.path(), {
			from: navigationState.from,
			incomeBackTo: IncomeBackTo.Overview,
			keepDraft: true,
		});

	return (
		<FinancesWizardLayout
			backRoute={Routing.Finances.Expenses.path()}
			backState={{
				...navigationState,
				incomeBackTo:
					navigationState.incomeBackTo === IncomeBackTo.Overview
						? IncomeBackTo.Intro
						: navigationState.incomeBackTo,
			}}
		>
			<BalanceHero
				title={
					positive ? t(R.Result.surplusHeading) : t(R.Result.deficitHeading)
				}
				balance={balance}
				size="lg"
			/>

			<SectionHeader title={t(R.Result.summary)} />
			<Stack bgcolor={theme.palette.background.paper}>
				<BalanceBreakdownRow
					variant="total"
					icon={
						<ArrowUpRight
							size={theme.iconSize.md}
							color={theme.palette.green[500]}
						/>
					}
					label={t(R.Result.income)}
					amount={totalIncome}
				/>
				<BalanceBreakdownRow
					variant="total"
					icon={
						<ArrowDownRight
							size={theme.iconSize.md}
							color={theme.palette.red[500]}
						/>
					}
					label={t(R.Result.expenses)}
					amount={totalExpenses}
					isLast
				/>
			</Stack>

			<BalanceRecommendation
				positive={positive}
				description={
					positive
						? t(R.Recommendation.resultSurplusDescription)
						: t(R.Recommendation.resultDeficitDescription)
				}
			/>

			<FooterActions
				primaryLabel={t(R.Result.finishButton)}
				onPrimary={finishFinances}
				isPrimaryLoading={isPending}
				secondaryLabel={t(R.Result.checkButton)}
				onSecondary={goToIncome}
			/>
			<LeaveFinancesDrawer open={isLeaving} onStay={stay} onLeave={leave} />
		</FinancesWizardLayout>
	);
};
